import { useEffect, useMemo, useRef, useState } from 'react';

import BeefreeSDK from '@beefree.io/sdk';
import { TokenStatus } from '@beefree.io/sdk/dist/types/bee';
import BeePlugin from '@mailupinc/bee-plugin';
import { EmailOutlined, FunctionsOutlined, Grading, GridOff, GridOn, PageviewOutlined } from '@mui/icons-material';
import IosShareIcon from '@mui/icons-material/IosShare';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { Badge, IconButton, Stack, Tooltip } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';

import AssessmentDialog from 'domains/content/components/Bee/AssessmentDialog/AssessmentDialog';
import {
    ApplicationAccessType,
    BeeEmailEditorConfig,
    CommunicationType,
    ContentTypeForComparison,
    Template,
} from 'domains/content/types';
import Button from 'domains/core/components/Button';
import PageHeader from 'domains/core/components/PageHeader';
import ErrorSnackbar from 'domains/core/components/Snackbars/ErrorSnackbar';
import SuccessSnackbar from 'domains/core/components/Snackbars/SuccessSnackbar';
import PageInfoIcon from 'domains/core/components/SvgIcons/PageInfoIcon';
import SendIcon from 'domains/core/components/SvgIcons/SendIcon';
import { RowDisplayCondition } from 'hooks/queries/useDisplayConditions';
import useFeatureSettings from 'hooks/queries/useFeatureSettings';
import { useAccessCentrePermissions } from 'hooks/useAccessCentrePermissions';
import useQueryParams from 'hooks/useQueryParams';
import ContentDetailsPage from 'pages/ContentDetailsPage';
import { ContentStatus } from 'pages/ContentPage/ContentTable/ContentStatusText/ContentStatusText';

import ContentTaggingButtonWithDialog from '../ContentTaggingButtonWithDialog/ContentTaggingButtonWithDialog';

import AssessmentPreviewDialog from './AssessmentPreviewDialog';
import ConditionsBuilderDialog from './ConditionsBuilderDialog';
import ManageDisplayConditionsModal from './ManageDisplayConditions';

const beeStyles = {
    width: '100%',
    height: '100%',
    overflow: 'scroll',
    marginTop: '20px',
};

const DEFAULT_CONFIGURATION = {
    container: 'bee-plugin-container', // Identifies the id of div element that contains BEE Plugin.
    language: 'en-US',
    sidebarPosition: 'left',
};

type Props = {
    applicationsAccess?: ApplicationAccessType[];
    beeConfig: BeeEmailEditorConfig;
    beeJson: object;
    communicationType?: CommunicationType;
    handleApplicationsAccess?: (event: any) => void;
    handleCommunicationType?: (event: any) => void;
    isSuccess: boolean;
    /**
     * When switching between creating and editing a template/landing page, isLoading will be true and cause the Bee Editor to restart.
     * For that particular use case, pass noRestart=true.
     */
    isNoRestart?: boolean;
    isSaving?: boolean;
    showCustomToolbar?: boolean;
    template?: Template;
    onAutoSave?: (beeJson: string) => void;
    onChange?: (beeJson: string, response: object) => void;
    onSave?: (beeJson: string) => void;
    onStatusChange?: (status: ContentStatus) => void;
    openSendTestDialog?: () => void;
    setIsNoRestart?: (isNoRestart: boolean) => void;
    hasDynamicContent?: boolean;
    hasUsedDisplayConditions?: boolean;
    isExportLoading?: boolean;
    onExportClick?: () => void;
    isExportPdfLoading?: boolean;
    onExportPdfClick?: () => void;
    rowDisplayConditions?: RowDisplayCondition[];
    onBeeChangeFromCommunicationType?: (beeJson: string, beeEditorRef: any) => void;
    /** When set to a non-null value, loads this JSON into the running editor via the SDK's .load() API. Used for language switching without restarting the editor. */
    contentToLoad?: object | null;
    /** Called whenever the editor's preview mode changes so the parent can react (e.g. disable language tab bar). */
    onPreviewModeChange?: (isPreview: boolean) => void;
};

// TODO: rename this component ContentBuilder as it no longer only returns the BeePlugin
const BeeEditor = ({
    applicationsAccess,
    beeConfig,
    beeJson,
    communicationType,
    handleApplicationsAccess,
    handleCommunicationType,
    isSuccess,
    isNoRestart = false,
    isSaving = false,
    showCustomToolbar = true,
    template,
    onAutoSave,
    onChange,
    onSave,
    onStatusChange,
    openSendTestDialog,
    setIsNoRestart,
    hasDynamicContent,
    hasUsedDisplayConditions = false,
    isExportLoading = false,
    onExportClick,
    isExportPdfLoading = false,
    onExportPdfClick,
    rowDisplayConditions = [],
    onBeeChangeFromCommunicationType,
    contentToLoad = null,
    onPreviewModeChange,
}: Props) => {
    const beeEditorRef = useRef(null);
    const {
        contentDraft: hasContentDraft,
        emailHtmlExport: hasEmailHtmlExport,
        emailPdfExport: hasEmailPdfExport,
        manageDisplayConditions: hasManageDisplayConditions,
    } = useFlags();
    const { canPublishContent, canManageContent } = useAccessCentrePermissions();
    const query = useQueryParams();
    const id = query.get('id');
    const isTemplateDefault = id === 'default';
    const roleHash = 'defaultrole';

    const [isAssessmentDialogVisible, setIsAssessmentDialogVisible] = useState(false);
    const [isAssessmentPreviewDialogVisible, setIsAssessmentPreviewDialogVisible] = useState(false);
    const [isDisplayConditionsDialogVisible, setIsDisplayConditionsDialogVisible] = useState(false);
    const [isManageDisplayConditionsModalVisible, setIsManageDisplayConditionsModalVisible] = useState(false);
    const [isBeeLoaded, setIsBeeLoaded] = useState(false);
    const [isDetailsActive, setIsDetailsActive] = useState(false);
    const [isDetailsExitBlocked, setIsDetailsExitBlocked] = useState({ value: false, key: 0 });
    const [isDetailsSaveSuccess, setIsDetailsSaveSuccess] = useState(false);
    const [isDetailsTouched, setIsDetailsTouched] = useState(false);
    const [isGridVisible, setIsGridVisible] = useState(false);
    const [isPreviewMode, setIsPreviewMode] = useState(false);
    const [displayConditionSuccessMessage, setDisplayConditionSuccessMessage] = useState('');
    const [displayConditionErrorMessage, setDisplayConditionErrorMessage] = useState('');
    const [notificationKey, setNotificationKey] = useState(0);
    const { data: featureSettings } = useFeatureSettings();
    const onLoad = () => setIsBeeLoaded(true);

    const savedRowsExternalUrlsKey = useMemo(
        () =>
            JSON.stringify(
                (beeConfig.rowsConfiguration?.externalContentURLs ?? []).map(
                    (entry: { handle?: string }) => entry?.handle ?? ''
                )
            ),
        [beeConfig.rowsConfiguration?.externalContentURLs]
    );

    // Saved-row category tabs can grow after distinct categories load; refresh Rows config without a full restart.
    useEffect(() => {
        if (!isBeeLoaded || !beeEditorRef.current || !beeConfig.rowsConfiguration) {
            return;
        }
        beeEditorRef.current.loadConfig({
            rowsConfiguration: beeConfig.rowsConfiguration,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isBeeLoaded, savedRowsExternalUrlsKey]);

    // Keep a ref to the latest isPreviewMode so the contentToLoad effect can read it
    // without adding isPreviewMode to its deps (which would trigger an extra load on every preview toggle).
    const isPreviewModeRef = useRef(isPreviewMode);
    useEffect(() => {
        isPreviewModeRef.current = isPreviewMode;
        onPreviewModeChange?.(isPreviewMode);
    }, [isPreviewMode, onPreviewModeChange]);

    // Load new content into the running editor instance without restarting it.
    // Used when switching between language tabs — avoids the full restart cycle.
    // When in preview mode the SDK doesn't update the preview on .load(), so we
    // briefly exit preview, load the content, then re-enter preview.
    useEffect(() => {
        if (contentToLoad != null && beeEditorRef.current && isBeeLoaded) {
            if (isPreviewModeRef.current) {
                beeEditorRef.current.togglePreview(); // exit preview
                beeEditorRef.current.load(contentToLoad);
                beeEditorRef.current.togglePreview(); // re-enter preview
            } else {
                beeEditorRef.current.load(contentToLoad);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contentToLoad]);
    const hasNonOutreachApps = applicationsAccess?.some((app) => app !== ApplicationAccessType.OUTREACH);

    const createDisplayConditions = () => {
        if (hasManageDisplayConditions) {
            setIsManageDisplayConditionsModalVisible(true);
        } else {
            setIsDisplayConditionsDialogVisible(true);
        }
    };

    const handleDisplayConditionSuccess = () => {
        setDisplayConditionSuccessMessage('Display condition created successfully.');
        setDisplayConditionErrorMessage('');
        setNotificationKey((prev) => prev + 1);
    };

    const handleDisplayConditionError = (error: Error) => {
        setDisplayConditionErrorMessage(error.message || 'Failed to create display condition.');
        setDisplayConditionSuccessMessage('');
        setNotificationKey((prev) => prev + 1);
    };

    // Dynamically update display conditions configuration without editor restart
    const updateDisplayConditionsConfig = () => {
        if (beeEditorRef.current && isBeeLoaded) {
            const shouldHideDisplayConditions = hasNonOutreachApps;
            const filteredRowDisplayConditions = shouldHideDisplayConditions ? [] : rowDisplayConditions;

            // Update configuration dynamically - no restart needed!
            beeEditorRef.current.loadConfig({
                rowDisplayConditions: filteredRowDisplayConditions,
                advancedPermission: {
                    rows: {
                        displayConditions: {
                            show: hasDynamicContent && !shouldHideDisplayConditions,
                            locked: !hasDynamicContent || shouldHideDisplayConditions,
                        },
                    },
                },
                enable_display_conditions: hasDynamicContent && !shouldHideDisplayConditions,
            });
        }
    };

    useEffect(() => {
        // Use our dynamic config update function - just like unsubscribe removal!
        updateDisplayConditionsConfig();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isBeeLoaded, rowDisplayConditions, hasNonOutreachApps, hasDynamicContent]);

    useEffect(() => {
        const customCss = `${window.location.origin}/bee.css`;

        const shouldHideDisplayConditions = hasNonOutreachApps;
        const filteredRowDisplayConditions = shouldHideDisplayConditions ? [] : rowDisplayConditions;

        const beeFinalConfig = {
            ...DEFAULT_CONFIGURATION,
            onAutoSave,
            onChange,
            onLoad,
            onSave: canManageContent ? onSave : undefined,
            onSend: openSendTestDialog,
            uid: beeConfig.client_uid,
            customCss,
            roleHash,
            mergeTags: beeConfig.mergeTags,
            specialLinks: beeConfig.specialLinks,
            editorFonts: { ...beeConfig.editorFonts },
            autosave: beeConfig.autosave,
            ...beeConfig,
            advancedPermission: {
                rows: {
                    displayConditions: {
                        show: hasDynamicContent && !shouldHideDisplayConditions,
                        locked: !hasDynamicContent || shouldHideDisplayConditions,
                    },
                },
            },
            enable_display_conditions: hasDynamicContent && !shouldHideDisplayConditions,
            defaultForm: {
                structure: {
                    title: 'Form title',
                    fields: {
                        firstName: { type: 'text', label: 'First Name' },
                        lastName: { type: 'text', label: 'Last Name' },
                        email: { type: 'email', label: 'Email' },
                        submit: { type: 'submit', label: '', attributes: { value: 'Submit' } },
                    },
                    layout: [['firstName', 'lastName', 'email', 'submit']],
                },
            },
            rowDisplayConditions: filteredRowDisplayConditions,
        };

        if (isSuccess && !isNoRestart) {
            try {
                beeEditorRef.current = hasDynamicContent
                    ? new BeefreeSDK({
                          access_token: beeConfig.access_token,
                          v2: true,
                          status: TokenStatus.OK,
                          shared: false,
                          coediting_session_id: null,
                      })
                    : new BeePlugin(beeConfig);

                beeEditorRef.current.start(beeFinalConfig, beeJson);
            } catch (error) {}
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSuccess]);

    const togglePreviewAndUpdateMode = (isPreview: boolean) => {
        if (!!beeEditorRef.current) {
            beeEditorRef.current.togglePreview();
        }
        setIsPreviewMode(isPreview);
    };

    const handleClickButton = (isPreview: boolean) => {
        setIsDetailsSaveSuccess(false);

        if (isPreview !== isPreviewMode) {
            togglePreviewAndUpdateMode(isPreview);
        }

        if (isDetailsTouched) {
            setIsDetailsExitBlocked((prevState) => ({ value: true, key: prevState.key + 1 }));
        } else {
            setIsDetailsActive(false);
        }
    };

    const editEmailButton = (
        <Tooltip title="Edit Email" placement="top">
            <span>
                <IconButton
                    aria-label="Edit Email"
                    size="large"
                    onClick={() => handleClickButton(false)}
                    disabled={!beeEditorRef.current || !isBeeLoaded}
                >
                    <EmailOutlined />
                </IconButton>
            </span>
        </Tooltip>
    );

    const beeEditorPreviewButton = (
        <Tooltip title="Preview" placement="top">
            <span>
                <IconButton
                    aria-label="Preview"
                    size="large"
                    onClick={() => handleClickButton(true)}
                    disabled={!beeEditorRef.current || !isBeeLoaded}
                >
                    <PageviewOutlined />
                </IconButton>
            </span>
        </Tooltip>
    );

    const assessmentButton = (
        <Tooltip title="Select Assessment" placement="top">
            <span>
                <IconButton
                    aria-label="Select Assessment"
                    size="large"
                    color="primary"
                    onClick={() => setIsAssessmentDialogVisible(true)}
                >
                    {isSuccess && template?.assessmentId && template.assessmentName ? (
                        <Badge color="success" variant="dot">
                            <Grading />
                        </Badge>
                    ) : (
                        <Grading />
                    )}
                </IconButton>
            </span>
        </Tooltip>
    );

    const renderToolbar = () => {
        const sendDetailsButton = (
            <Tooltip title="Send Details" placement="top">
                <IconButton aria-label="Send Details" size="large" onClick={() => setIsDetailsActive(true)}>
                    <PageInfoIcon />
                </IconButton>
            </Tooltip>
        );

        const sendTestButton = (
            <Tooltip title="Send Test" placement="top">
                <IconButton
                    aria-label="Send Test"
                    size="large"
                    data-testid="send-test-icon-button"
                    onClick={() => openSendTestDialog()}
                >
                    <SendIcon />
                </IconButton>
            </Tooltip>
        );

        const displayConditionsButtonLabel = hasManageDisplayConditions
            ? 'Manage Display Conditions'
            : 'Create Display Conditions';

        const createDisplayConditionsButton = (
            <Tooltip
                title={
                    hasNonOutreachApps
                        ? 'Emails with dynamic content can only be used in the Outreach application'
                        : displayConditionsButtonLabel
                }
                placement="top"
            >
                <span>
                    <IconButton
                        aria-label={displayConditionsButtonLabel}
                        size="large"
                        onClick={createDisplayConditions}
                        disabled={!!hasNonOutreachApps}
                    >
                        <FunctionsOutlined />
                    </IconButton>
                </span>
            </Tooltip>
        );

        if (isPreviewMode) {
            return (
                <PageHeader
                    icon={<PageviewOutlined fontSize="inherit" />}
                    header="preview"
                    headerOthers={
                        <Stack direction="row" justifyContent="space-between">
                            <Stack direction="row" alignItems="center" columnGap={1} ml={1}>
                                {editEmailButton}
                                {!isTemplateDefault && sendDetailsButton}
                                {sendTestButton}
                            </Stack>
                        </Stack>
                    }
                />
            );
        }

        const toggleGridVisibility = () => {
            !!beeEditorRef.current && beeEditorRef.current.toggleStructure();
            setIsGridVisible(!isGridVisible);
        };
        return (
            <PageHeader
                icon={<EmailOutlined fontSize="inherit" />}
                header="email"
                headerOthers={
                    <Stack direction="row" justifyContent="space-between" width="100%">
                        <Stack direction="row" alignItems="center" columnGap={1} ml={1}>
                            {!isTemplateDefault && sendDetailsButton}
                            {beeEditorPreviewButton}
                            {hasDynamicContent && createDisplayConditionsButton}
                            {featureSettings?.assessments_enabled && assessmentButton}
                            <Tooltip title={isGridVisible ? 'Hide Grid' : 'Show Grid'} placement="top">
                                <span>
                                    <IconButton
                                        aria-label={isGridVisible ? 'Hide Grid' : 'Show Grid'}
                                        size="large"
                                        onClick={toggleGridVisibility}
                                        disabled={!beeEditorRef.current || !isBeeLoaded}
                                    >
                                        {isGridVisible ? <GridOff /> : <GridOn />}
                                    </IconButton>
                                </span>
                            </Tooltip>
                            {sendTestButton}
                            <ContentTaggingButtonWithDialog
                                beeJson={JSON.stringify(beeJson)}
                                channel={ContentTypeForComparison.EMAIL}
                                handleApplicationsAccess={handleApplicationsAccess}
                                onBeeChangeFromCommunicationType={(beeJson: string) =>
                                    onBeeChangeFromCommunicationType(beeJson, beeEditorRef)
                                }
                                handleCommunicationType={handleCommunicationType}
                                selectedApplicationsAccess={applicationsAccess}
                                selectedCommunicationType={communicationType}
                                hasDynamicContent={hasUsedDisplayConditions}
                            />
                        </Stack>
                        <Stack direction="row" alignItems="center" columnGap={1} ml={1}>
                            {hasEmailHtmlExport && onExportClick && (
                                <Tooltip title="Export as HTML" placement="top">
                                    <span>
                                        <Button
                                            disabled={isTemplateDefault || isExportLoading || isSaving || !isBeeLoaded}
                                            onClick={onExportClick}
                                            variant="outlined"
                                            data-testid="export-html-button"
                                        >
                                            <IosShareIcon />
                                        </Button>
                                    </span>
                                </Tooltip>
                            )}
                            {hasEmailPdfExport && onExportPdfClick && (
                                <Tooltip title="Export as PDF" placement="top">
                                    <span>
                                        <Button
                                            aria-label="Export as PDF"
                                            disabled={
                                                isTemplateDefault || isExportPdfLoading || isSaving || !isBeeLoaded
                                            }
                                            onClick={onExportPdfClick}
                                            variant="outlined"
                                            data-testid="export-pdf-button"
                                        >
                                            <PictureAsPdfIcon />
                                        </Button>
                                    </span>
                                </Tooltip>
                            )}
                            {canManageContent && (
                                <Button
                                    disabled={
                                        !beeEditorRef.current ||
                                        !isBeeLoaded ||
                                        isSaving ||
                                        isExportLoading ||
                                        isExportPdfLoading
                                    }
                                    onClick={() => {
                                        if (hasContentDraft) {
                                            onStatusChange?.(ContentStatus.DRAFT);
                                        }
                                        beeEditorRef.current?.save();
                                    }}
                                    variant="outlined"
                                    data-testid="save-exit-button"
                                    role="button"
                                >
                                    Save & Exit
                                </Button>
                            )}
                            {hasContentDraft && canPublishContent && (
                                <Button
                                    disabled={
                                        !beeEditorRef.current ||
                                        !isBeeLoaded ||
                                        isSaving ||
                                        isExportLoading ||
                                        isExportPdfLoading
                                    }
                                    onClick={() => {
                                        onStatusChange?.(ContentStatus.PUBLISHED);
                                        beeEditorRef.current?.save();
                                    }}
                                    variant="contained"
                                    data-testid="publish-button"
                                    role="button"
                                >
                                    Publish
                                </Button>
                            )}
                        </Stack>
                    </Stack>
                }
            />
        );
    };

    const shouldRenderToolbar = showCustomToolbar && !isDetailsActive;

    return (
        <>
            {shouldRenderToolbar && renderToolbar()}
            <div
                // The id is required to render the plugin.
                id="bee-plugin-container"
                data-test="bee-plugin-container"
                style={{
                    ...beeStyles,
                    display: isDetailsActive ? 'none' : 'block',
                }}
                className="beeContainer"
            />
            {featureSettings?.assessments_enabled && (
                <AssessmentDialog
                    applicationsAccess={applicationsAccess}
                    communicationType={communicationType}
                    content={template}
                    isDialogVisible={isAssessmentDialogVisible}
                    onAssessmentPreview={() => setIsAssessmentPreviewDialogVisible(true)}
                    setIsDialogVisible={setIsAssessmentDialogVisible}
                    setBeeEditorIsNoRestart={setIsNoRestart}
                    onStatusChange={onStatusChange}
                />
            )}
            {featureSettings?.assessments_enabled && (
                <AssessmentPreviewDialog
                    isOpen={isAssessmentPreviewDialogVisible}
                    handleClose={() => setIsAssessmentPreviewDialogVisible(false)}
                />
            )}
            <ConditionsBuilderDialog
                isDialogVisible={isDisplayConditionsDialogVisible}
                setIsDialogVisible={setIsDisplayConditionsDialogVisible}
                onCreateConditionSuccess={handleDisplayConditionSuccess}
                onCreateConditionError={handleDisplayConditionError}
            />
            <ManageDisplayConditionsModal
                isDialogVisible={isManageDisplayConditionsModalVisible}
                setIsDialogVisible={setIsManageDisplayConditionsModalVisible}
            />
            {displayConditionSuccessMessage && (
                <SuccessSnackbar key={`success-${notificationKey}`} successMessage={displayConditionSuccessMessage} />
            )}
            {displayConditionErrorMessage && (
                <ErrorSnackbar key={`error-${notificationKey}`} errorMessage={displayConditionErrorMessage} />
            )}
            {isDetailsActive && (
                <ContentDetailsPage
                    applicationsAccess={applicationsAccess}
                    beeEditorPreviewButton={beeEditorPreviewButton}
                    communicationType={communicationType}
                    editEmailButton={editEmailButton}
                    isDetailsExitBlocked={isDetailsExitBlocked}
                    isDetailsTouched={isDetailsTouched}
                    setIsDetailsExitBlocked={setIsDetailsExitBlocked}
                    setIsDetailsTouched={setIsDetailsTouched}
                    setIsDetailsActive={setIsDetailsActive}
                    setIsDetailsSaveSuccess={setIsDetailsSaveSuccess}
                />
            )}
            {isDetailsSaveSuccess && <SuccessSnackbar successMessage="Send Details Saved." />}
        </>
    );
};

export default BeeEditor;
